<template>
  <div class="success-container">
    <v-btn
      class="close-button"
      dense
      text
      icon
      :disabled="startingOver"
      @click="startNewOrder"
    >
      <v-icon color="black" dense>mdi-close</v-icon>
    </v-btn>
    <div v-if="isLoading">
      <v-progress-circular indeterminate />
    </div>
    <div v-else>
      <div class="success-content-container">
        <div class="success-card" data-cy="payment-success-screen">
          <lottie-vue-player
            src="/assets/order-status-animations/successful-payment.json"
            style="width: 120px; height: 120px; margin-top: -50px"
            autoplay
          />
          <div class="my-10 text-center">
            <h3 class="header-text mb-10">
              {{ $t('kiosk.success.title', { orderId }) }}
            </h3>
            <span>{{ $t('kiosk.success.content') }}</span>
          </div>
        </div>
      </div>
      <div class="cta-container">
        <CountDownButton
          dark
          with-progress
          :loading="startingOver"
          :stop="anyModalOpen"
          @onTimeoutEnd="startNewOrder"
          @click="startNewOrder"
        >
          {{ $t('kiosk.success.cta') }}
        </CountDownButton>
        <SmsPhoneModalTrigger
          v-if="showSMSModal"
          :session="session"
          @open="smsModalOpen = true"
          @close="smsModalOpen = false"
        />
        <RequestReceiptButton
          v-if="payment"
          link
          :session="session"
          link-wrapper-class="mt-0"
          :payment-uuid="paymentUUID"
          @on-open="requestReceiptModalOpen = true"
          @on-close="requestReceiptModalOpen = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CountDownButton from '@/components/common/CountDownButton.vue';
import RequestReceiptButton from '@/components/Payments/RequestReceiptButton.vue';
import SmsPhoneModalTrigger from '@/components/Orders/SmsPhoneModalTrigger.vue';

export default {
  components: {
    CountDownButton,
    RequestReceiptButton,
    SmsPhoneModalTrigger,
  },
  data() {
    return {
      session: null,
      isLoading: true,
      payment: {},
      uniqueOrderNumber: null,
      paymentUUID: null,
      startingOver: false,
      requestReceiptModalOpen: false,
      smsModalOpen: false,
    };
  },
  computed: {
    anyModalOpen() {
      return this.requestReceiptModalOpen || this.smsModalOpen;
    },
    functionalityEnabled() {
      return this.$store.getters
        .functionalitySMSNotificationsEnabledForPayInPerson;
    },
    pendingPhone() {
      return this.$store.getters.getPendingPayInPersonPhone;
    },
    hasSessionPhone() {
      return this.$store.getters.getSessionPhoneNumber;
    },
    amountFormatted() {
      return this.$store.getters.getFormattedPrice(this.payment.amount);
    },
    paymentId() {
      return this.payment.id;
    },
    orderId() {
      const { getOrderByUniqueOrderNumber } = this.$store.getters;
      const order = getOrderByUniqueOrderNumber(this.uniqueOrderNumber);

      return order?.order_id;
    },
    showSMSModal() {
      return (
        this.functionalityEnabled &&
        this.session &&
        !this.hasSessionPhone &&
        !this.pendingPhone
      );
    },
  },
  async mounted() {
    const session = this.$route.params.session;
    const paymentUUID = this.$route.params.paymentUUID;
    const uniqueOrderNumber = this.$route.query.orderNumber;

    await this.$store.dispatch('fetchSessionData', { session });
    const payment = await this.$store.dispatch('fetchPaymentByUUID', {
      paymentUUID,
    });
    this.$store.dispatch('fetchOrders');
    this.payment = payment;
    this.uniqueOrderNumber = uniqueOrderNumber;
    this.paymentUUID = paymentUUID;
    this.session = session;

    this.isLoading = false;

    this.maybePersistPendingPhone();
  },
  methods: {
    async maybePersistPendingPhone() {
      if (
        !this.functionalityEnabled ||
        !this.session ||
        !this.pendingPhone ||
        this.hasSessionPhone
      ) {
        return;
      }

      const { countryCallingCode, nationalNumber } = this.pendingPhone || {};

      if (!countryCallingCode || !nationalNumber) {
        return;
      }

      try {
        await this.axios.post(`/session/${this.session}/add-phone-number`, {
          countryCallingCode,
          nationalNumber,
        });

        this.$store.dispatch(
          'setSessionPhoneNumber',
          `+${countryCallingCode}${nationalNumber}`
        );
      } catch (error) {
        console.error('Failed to save phone number:', error);
      } finally {
        this.$store.dispatch('clearPendingPayInPersonPhone');
      }
    },
    startNewOrder() {
      this.startingOver = true;
      this.$store.dispatch('generateNewKioskSession');
      this.$store.dispatch('clearPendingPayInPersonPhone');
    },
  },
};
</script>

<style lang="scss" scoped>
.success-container {
  min-height: 100vh;
  justify-content: center;
  align-items: center;
  display: flex;
  animation: backgroundFill 3s normal forwards;
  background-size: 400% 400%;
}

.success-content-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  width: 100%;
  margin-bottom: 40px;
  padding: 40px;
}

.success-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.header-text {
  font-size: 32px;
  font-weight: 600;
  color: #489f31;
}

.h-100 {
  height: 100%;
}

.cta-container {
  display: flex;
  flex-direction: column;
  gap: 35px;
  padding: 0 40px;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
}

@keyframes backgroundFill {
  from {
    background-color: rgba(255, 255, 255, 0);
  }
  to {
    background-color: rgba(72, 159, 49, 0.6);
  }
}
</style>
